import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ApplicationsAndPolicies from "../components/applicationsAndPolicies";
import {
	changeApplicationsAndPoliciesField,
	clearApplicationsAndPoliciesFilters,
	receiveGetAllApplications,
	requestGetAllApplications,
	requestGetAllApplicationsSuggestions,
	requestGlobalPolicySearch,
} from '../actions/applicationsAndPolicies';
import { isAragUser, isSolicitorUser, changePage, changePageSize, goAtPrevious, goAtNext, goAtStart, goAtEnd, goAtPage } from "../helpers/functions";
import { getCaseTypes } from "../actions/caseTypes";
import { getSolicitorGroupCaseTypes } from "../actions/groups";
import { requestGetApplicationStatuses } from "../actions/applicationStatuses";
import { getSolicitorUserColleagues, requestGetSolicitors } from "../actions/agentsList";
import { DRAFT, GLOBAL_POLICY_SEARCH_MIN_CHARACTERS, POLICY_SOURCE } from '../helpers/constants';
import useFeatureFlag from '../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '../constants/configurationKeys';
import CONTENT_STRINGS from '../helpers/contentStrings';
import { openExternalPolicyUpdateModal } from '../reducers/externalPolicyUpdate';
import ExternalPolicyUpdateHOC from './ExternalPolicy/externalPolicyUpdateHOC';

const ApplicationsAndPoliciesHOC = () => {
	const dispatch = useDispatch();
	const history = useHistory();

	const applicationsAndPolicies = useSelector(state => state.applicationsAndPolicies);
	const user = useSelector(state => state.user);
	const caseTypes = useSelector(state => state.caseTypes);
	const solicitorCaseTypes = useSelector(state => state.newApplication.caseTypes);
	const solicitorSubCaseTypes = useSelector(state => state.newApplication.subCaseTypes);
	const solicitorUsers = useSelector(state => state.agentsList.displayedUsers);
	const applicationStatuses = useSelector(state => state.applicationStatuses.statuses);

	const [fetchingSuggestions, setFetchingSuggestions] = useState(false);

	// Refs
	const askServerRef = useRef(null);
	const prevValueRef = useRef(applicationsAndPolicies.value);
	const prevWasFilterButtonPressedRef = useRef(applicationsAndPolicies.wasFilterButtonPressed);
	const isInitialRenderRef = useRef(true);
	const isInitialRenderForSubCaseTypesRef = useRef(true);
	const prevSuggestionsRef = useRef(applicationsAndPolicies.suggestions);
	const globalSearchTimeoutRef = useRef(null);
	const prevGlobalSearchValueRef = useRef(applicationsAndPolicies.globalSearchValue);

	const isArag = isAragUser(user.loggedInUser.roles);
	const isSolicitor = isSolicitorUser(user.loggedInUser.roles);

	const getParameters = (searchParameter) => {
		const calcOffset = applicationsAndPolicies.selectedView * (applicationsAndPolicies.page - 1);
		const selectedCaseTypes = applicationsAndPolicies.selectedCaseTypes;
		const selectedSubCaseTypes = applicationsAndPolicies.selectedSubCaseTypes;
		let selectedStatuses = [...applicationsAndPolicies.selectedStatuses];

		if (applicationsAndPolicies.includeDraft && isArag) {
			const draftStatus = applicationStatuses.filter(x => x.machineName == DRAFT);
			const exists = selectedStatuses.filter(x => x.machineName == DRAFT);
			if (exists.length == 0 && draftStatus.length > 0) {
				selectedStatuses.push(draftStatus[0]);
			}
		} else if (!applicationsAndPolicies.includeDraft && isArag && selectedStatuses.length > 0) {
			selectedStatuses = selectedStatuses.filter(x => x.machineName !== DRAFT);
		}

		return {
			limit: applicationsAndPolicies.selectedView,
			offset: calcOffset > 0 ? calcOffset : 0,
			shallow: true,
			selectedStatuses: selectedStatuses,
			search: searchParameter,
			selectedCaseTypes: selectedCaseTypes,
			selectedSubCaseTypes: selectedSubCaseTypes,
			lawsysId: applicationsAndPolicies.selectedSolicitorFirm ? applicationsAndPolicies.selectedSolicitorFirm : null,
			selectedLawsys: applicationsAndPolicies.selectedSolicitorFirms,
			selectedSolicitorUsers: applicationsAndPolicies.selectedSolicitorUsers,
		};
	};

	const onChange = (newValue, actionMeta) => {
		if (actionMeta.action === 'input-change') {
			dispatch(changeApplicationsAndPoliciesField('value', newValue || ''));
		}
	};

	const onAppSuggestionSelected = (selectedOption) => {
		if (!selectedOption) return;
		let newValue = '';
		if (selectedOption.aragReference !== '' && selectedOption.aragReference !== 0) {
			newValue = selectedOption.aragReference;
		} else if (selectedOption.clientName !== '') {
			newValue = selectedOption.clientName;
		} else if (selectedOption.solicitorFileReference !== '') {
			newValue = selectedOption.solicitorFileReference;
		}
		const payload = {
			data: [selectedOption],
			limit: 0,
			offset: 0,
			totalRecords: 1
		};
		dispatch(receiveGetAllApplications(payload));
		dispatch(changeApplicationsAndPoliciesField('value', newValue ? newValue.toString() : ''));
		dispatch(changeApplicationsAndPoliciesField('applications', [selectedOption]));
	};

	const handleAutosuggestButton = () => {
		const parameters = getParameters(applicationsAndPolicies.value);
		dispatch(requestGetAllApplications(parameters));
	};

	const handlePageChange = changePage(dispatch, changeApplicationsAndPoliciesField);
	const handlePageSizeChange = changePageSize(dispatch, changeApplicationsAndPoliciesField);
	const handleGoAtPrevious = goAtPrevious(handlePageChange, applicationsAndPolicies.page);
	const handleGoAtNext = goAtNext(handlePageChange, applicationsAndPolicies.page, applicationsAndPolicies.totalBoxes);
	const handleGoAtStart = goAtStart(handlePageChange);
	const handleGoAtEnd = goAtEnd(handlePageChange, applicationsAndPolicies.totalBoxes);
	const handleGoAtPage = goAtPage(handlePageChange);

	const handleSolicitorFirmsFilterChange = (values) => {
		dispatch(changeApplicationsAndPoliciesField('selectedSolicitorFirms', values));
	};

	const handleSolicitorUsersFirmsFilterChange = (values) => {
		dispatch(changeApplicationsAndPoliciesField('selectedSolicitorUsers', values));
	};

	const handleStatusesFilterChange = (values) => {
		dispatch(changeApplicationsAndPoliciesField('selectedStatuses', values));
	};

	const handleCaseTypesFilterChange = (values) => {
		dispatch(changeApplicationsAndPoliciesField('selectedCaseTypes', values));
	};

	const handleSubCaseTypesFilterChange = (values) => {
		dispatch(changeApplicationsAndPoliciesField('selectedSubCaseTypes', values));
	};

	const getSolicitorUsersForMultipleSelect = () => {
		return solicitorUsers
			.filter(u => {
				return (
					applicationsAndPolicies.selectedSolicitorFirms.find(firm => firm.value === u.lawsysId)
					|| applicationsAndPolicies.selectedSolicitorFirms.length === 0
				);
			})
			.map(u => ({ value: u.userId, label: `${u.forename} ${u.surname}` }));
	};

	const handleFilterClick = () => {
		dispatch(changeApplicationsAndPoliciesField('wasFilterButtonPressed', true));
		const parameters = getParameters(
			applicationsAndPolicies.value === '' ? null : applicationsAndPolicies.value
		);
		dispatch(requestGetAllApplications(parameters));
	};

	const handleClearFilters = () => dispatch(clearApplicationsAndPoliciesFilters());

	const handleInputChange = (key, value) => dispatch(changeApplicationsAndPoliciesField(key, value));

	const handlePush = (path) => history.push(path);

	//Initial data fetch
	useEffect(() => {
		const parameters = getParameters(
			applicationsAndPolicies.value === '' ? null : applicationsAndPolicies.value
		);
		dispatch(requestGetAllApplications(parameters));
		dispatch(requestGetApplicationStatuses());

		if (isArag) {
			dispatch(getCaseTypes());
			dispatch(requestGetSolicitors({
				limit: 0,
				offset: 0,
				lawsysId: null,
				search: null,
				orderAsc: true,
				orderBySolicitorName: true
			}));
		}

		if (isSolicitor && user.loggedInUser.solicitorGroupId) {
			dispatch(getSolicitorGroupCaseTypes(user.loggedInUser.solicitorGroupId));
		}

		if (isSolicitor && user.loggedInUser.allowedToSeeAllFirmApplications) {
			dispatch(getSolicitorUserColleagues());
		}
	
	}, []);

	//React to search/page/view/filter changes
	useEffect(() => {
		if (isInitialRenderRef.current) {
			isInitialRenderRef.current = false;
			prevValueRef.current = applicationsAndPolicies.value;
			prevWasFilterButtonPressedRef.current = applicationsAndPolicies.wasFilterButtonPressed;
			return;
		}

		const prevValue = prevValueRef.current;
		const prevWasFilterButtonPressed = prevWasFilterButtonPressedRef.current;

		prevValueRef.current = applicationsAndPolicies.value;
		prevWasFilterButtonPressedRef.current = applicationsAndPolicies.wasFilterButtonPressed;

		const wasFilterTransition = prevWasFilterButtonPressed && !applicationsAndPolicies.wasFilterButtonPressed;
		const valueChanged = prevValue !== applicationsAndPolicies.value;

		// The original componentWillReceiveProps only entered this block when wasFilterButtonPressed
		// transitioned true→false. When it goes false→true, handleFilterClick already dispatches
		// the fetch, so we should skip to avoid a double-fetch.
		const wasFilterButtonPressedChanged = prevWasFilterButtonPressed !== applicationsAndPolicies.wasFilterButtonPressed;
		if (wasFilterButtonPressedChanged && !wasFilterTransition && !valueChanged) {
			return;
		}

		const parameters = getParameters(
			applicationsAndPolicies.value === '' ? null : applicationsAndPolicies.value
		);

		clearTimeout(askServerRef.current);

		if (valueChanged && applicationsAndPolicies.value.length >= GLOBAL_POLICY_SEARCH_MIN_CHARACTERS) {
			parameters.offset = 0;
			parameters.limit = GLOBAL_POLICY_SEARCH_MIN_CHARACTERS;
			setFetchingSuggestions(true);
			askServerRef.current = setTimeout(() => {
				dispatch(requestGetAllApplicationsSuggestions(parameters));
			}, 250);
		} else if (valueChanged && applicationsAndPolicies.value !== '' && applicationsAndPolicies.value.length < GLOBAL_POLICY_SEARCH_MIN_CHARACTERS) {
			setFetchingSuggestions(false);
			dispatch(changeApplicationsAndPoliciesField('suggestions', []));
		} else {
			dispatch(requestGetAllApplications(parameters));
		}

		return () => clearTimeout(askServerRef.current);
	}, [applicationsAndPolicies.selectedView, applicationsAndPolicies.value, applicationsAndPolicies.page, applicationsAndPolicies.wasFilterButtonPressed]);

	//Reset fetchingSuggestions when suggestions change
	useEffect(() => {
		if (prevSuggestionsRef.current !== applicationsAndPolicies.suggestions) {
			setFetchingSuggestions(false);
		}
		prevSuggestionsRef.current = applicationsAndPolicies.suggestions;
	}, [applicationsAndPolicies.suggestions]);

	// Global policy search - debounced dispatch
	useEffect(() => {
		const prevGlobalSearchValue = prevGlobalSearchValueRef.current;
		prevGlobalSearchValueRef.current = applicationsAndPolicies.globalSearchValue;

		if (prevGlobalSearchValue === applicationsAndPolicies.globalSearchValue) return;

		clearTimeout(globalSearchTimeoutRef.current);
		if ((applicationsAndPolicies.globalSearchValue || '').length >= 5) {
			globalSearchTimeoutRef.current = setTimeout(() => {
				dispatch(requestGlobalPolicySearch({
					searchTerm: applicationsAndPolicies.globalSearchValue,
					limit: 20
				}));
			}, 600);
		} else {
			dispatch(changeApplicationsAndPoliciesField('globalSearchSuggestions', []));
		}

		return () => clearTimeout(globalSearchTimeoutRef.current);
	}, [applicationsAndPolicies.globalSearchValue]);

	//Populate case type dropdown options
	useEffect(() => {
		const hasCaseTypes = isSolicitor ? solicitorCaseTypes.length > 0 : caseTypes.cases.length > 0;

		if (hasCaseTypes && applicationsAndPolicies.availableCaseTypes.length === 0 && applicationsAndPolicies.selectedCaseTypes.length === 0) {
			let caseTypeOptions = [];
			if (isArag) {
				caseTypeOptions = caseTypes.cases.map(item => ({
					value: item.id, label: item.name, machineName: item.machineName
				}));
			}
			if (isSolicitor) {
				caseTypeOptions = solicitorCaseTypes.map(item => ({
					value: item.id, label: item.name, machineName: item.machineName
				}));
			}
			dispatch(changeApplicationsAndPoliciesField('availableCaseTypes', caseTypeOptions));
		}
	}, [caseTypes.cases, solicitorCaseTypes, applicationsAndPolicies.availableCaseTypes, applicationsAndPolicies.selectedCaseTypes]);

	//Populate and filter sub-case type dropdown options
	useEffect(() => {
		const hasCaseTypes = isSolicitor ? solicitorCaseTypes.length > 0 : caseTypes.cases.length > 0;
		const hasSubCaseTypes = isSolicitor ? solicitorSubCaseTypes.length > 0 : caseTypes.subCases.length > 0;

		if (hasCaseTypes && hasSubCaseTypes
			&& applicationsAndPolicies.availableSubCaseTypes.length === 0
			&& applicationsAndPolicies.selectedSubCaseTypes.length === 0
			&& applicationsAndPolicies.selectedCaseTypes.length === 0) {
			let subCases = isSolicitor ? solicitorSubCaseTypes : caseTypes.subCases;
			const toUpdate = subCases.map(item => ({
				value: item.id, label: item.name, machineName: item.machineName
			}));
			dispatch(changeApplicationsAndPoliciesField('availableSubCaseTypes', toUpdate));
		}
	}, [caseTypes.cases, caseTypes.subCases, solicitorCaseTypes, solicitorSubCaseTypes, applicationsAndPolicies.availableSubCaseTypes, applicationsAndPolicies.selectedSubCaseTypes, applicationsAndPolicies.selectedCaseTypes]);

	//Re-filter sub-case types when case type selection changes
	useEffect(() => {
		if (isInitialRenderForSubCaseTypesRef.current) {
			isInitialRenderForSubCaseTypesRef.current = false;
			return;
		}

		let subCases = isSolicitor ? solicitorSubCaseTypes : caseTypes.subCases;
		if (applicationsAndPolicies.selectedCaseTypes.length > 0) {
			const caseTypesIds = applicationsAndPolicies.selectedCaseTypes.map(c => c.value);
			subCases = subCases.filter(c => caseTypesIds.includes(c.caseTypeId));
		}
		const toUpdate = subCases.map(item => ({
			value: item.id, label: item.name, machineName: item.machineName
		}));
		dispatch(changeApplicationsAndPoliciesField('availableSubCaseTypes', toUpdate));
	}, [applicationsAndPolicies.selectedCaseTypes, applicationsAndPolicies.selectedSubCaseTypes]);

	// Global policy search handlers
	const onGlobalSearchInputChange = (inputValue, actionMeta) => {
		if (actionMeta.action === 'input-change') {
			dispatch(changeApplicationsAndPoliciesField('globalSearchValue', inputValue || ''));
		}
	};

	const onGlobalSearchSelected = (selectedOption) => {
		if (!selectedOption) return;
		if (selectedOption.source === POLICY_SOURCE.GATEWAY && selectedOption.id) {
			history.push('/application-and-policies/application-policy/' + selectedOption.id);
		} else {
			dispatch(openExternalPolicyUpdateModal({
				policyNumber: selectedOption.policyNumber || '',
				solicitorReference: selectedOption.solicitorReference || '',
				clientName: selectedOption.clientName || '',
			}));
		}
	};

	const onGlobalSearchIconClick = () => {
		if ((applicationsAndPolicies.globalSearchValue || '').length >= 5) {
			dispatch(requestGlobalPolicySearch({
				searchTerm: applicationsAndPolicies.globalSearchValue,
				limit: 20
			}));
		}
	};

	const globalSearchOptions = (applicationsAndPolicies.globalSearchSuggestions || []).map(item => ({
		value: item.id,
		label: `${item.policyNumber ? item.policyNumber + ' | ' : ''}${item.solicitorReference ? item.solicitorReference + ' | ' : ''}${item.clientName || ''}`,
		dotColor: item.source === POLICY_SOURCE.GATEWAY ? 'yellow' : 'black',
		...item
	}));

	const suggestionOptions = applicationsAndPolicies.suggestions.map(item => ({
		value: item.id,
		label: `${item.solicitorReference ? item.solicitorReference + ' | ' : ''}${item.aragReference ? item.aragReference + ' | ' : ''}${item.clientName}`,
		...item
	}));

	const statusOptions = applicationStatuses
		.filter(item => item.name !== CONTENT_STRINGS.DRAFT)
		.map(item => ({
			machineName: item.machineName,
			value: item.name,
			label: item.name
		}));

	const statusOptionsForSolicitor = applicationStatuses.map(item => ({
		machineName: item.machineName,
		value: item.name,
		label: item.name
	}));

	const featureFlagGlobalPolicySearchEnabled = useFeatureFlag(FEATURE_FLAGS.GLOBAL_POLICY_SEARCH_ENABLED) && isSolicitor;
	const options = isArag ? statusOptions : statusOptionsForSolicitor;
	const isSearchLoading = applicationsAndPolicies.value.length > 0 && (applicationsAndPolicies.value.length < GLOBAL_POLICY_SEARCH_MIN_CHARACTERS || fetchingSuggestions);

	return (
		<>
			<ApplicationsAndPolicies
				applicationStatuses={applicationStatuses}
				clearApplicationsAndPoliciesFilters={handleClearFilters}
				handleFilterClick={handleFilterClick}
				handleCaseTypesFilterChange={handleCaseTypesFilterChange}
				handleSubCaseTypesFilterChange={handleSubCaseTypesFilterChange}
				handleStatusesFilterChange={handleStatusesFilterChange}
				handleSolicitorFirmsFilterChange={handleSolicitorFirmsFilterChange}
				caseTypesOptions={applicationsAndPolicies.availableCaseTypes}
				isAgentAllowedToSeeAllFirmApplications={user.loggedInUser.allowedToSeeAllFirmApplications}
				Store={applicationsAndPolicies}
				isArag={isArag}
				isSolicitor={isSolicitor}
				HandleAutosuggestButton={handleAutosuggestButton}
				onSuggestionSelected={onAppSuggestionSelected}
				onChange={onChange}
				suggestionOptions={suggestionOptions}
				fetchingSuggestions={fetchingSuggestions}
				HandleInputChange={handleInputChange}
				solicitorUsers={solicitorUsers}
				handleSolicitorUsersFirmsFilterChange={handleSolicitorUsersFirmsFilterChange}
				getSolicitorUsersForMultipleSelect={getSolicitorUsersForMultipleSelect}
				subCaseTypesOptions={applicationsAndPolicies.availableSubCaseTypes}
				onPageSizeChange={handlePageSizeChange}
				goAtPrevious={handleGoAtPrevious}
				goAtNext={handleGoAtNext}
				goAtStart={handleGoAtStart}
				goAtEnd={handleGoAtEnd}
				goAtPage={handleGoAtPage}
				push={handlePush}
				featureFlagGlobalPolicySearchEnabled={featureFlagGlobalPolicySearchEnabled}
				globalSearchOptions={globalSearchOptions}
				onGlobalSearchSelected={onGlobalSearchSelected}
				onGlobalSearchInputChange={onGlobalSearchInputChange}
				onGlobalSearchIconClick={onGlobalSearchIconClick}
				options={options}
				isSearchLoading={isSearchLoading}
			/>
			<ExternalPolicyUpdateHOC />
		</>
	);
};

export default ApplicationsAndPoliciesHOC;
